import { useEffect, useState } from "react";
import type { ReactElement } from "react";
import { Auswahl, Hinweis } from "@kundeninfo/ui/me-design";
import { useSofortPopup } from "@kundeninfo/ui";
import type { ApiClient, Konto, Kostenstelle } from "@kundeninfo/api-client";
import { KONTIERUNG_TEXT } from "./kontierungTexts.js";
import "./KontierungFelder.css";

/**
 * KONTIERUNGSFELDER (Spec „Wo kontiert wird und woher die Vorgabe kommt“, Plan Aufgabe 8): Konto und Kostenstelle an
 * einer Kontierungsstelle, je mit „ohne“. Das Konto kommt aus dem Rahmen des Unternehmens (`companyId`) und hat die Art
 * der Stelle — Aufwand an Vertrag und Eingangsposition, Erlös sonst —, die Kostenstelle aus den aktiven des
 * Unternehmens. Ein gesetzter Wert, der nicht (mehr) in der Auswahl steht (deaktiviert), wird einmal nachgeholt und
 * bleibt als Option „… (deaktiviert)“ sichtbar, eines anderen Kontenrahmens (Spec „Kontenliste“: vorhandene Kontierungen
 * bleiben stehen und werden gemeldet, Aufgabe 9) als „… (anderer Kontenrahmen)“ mit Hinweis am Feld; der Wert bleibt,
 * bis jemand anders wählt. `gesperrt` zeigt beide Felder lesend mit Sofort-Popup (übergebene Rechnung, C1). Darunter
 * leise: Die Kontierung wird erst mit dem DATEV-Export der Folgestufe übergeben. Felder als `Auswahl` wie der Bestand
 * für Stammdatenwahl (Steuersatz, Land) — Ruling Controller.
 */
export type KontierungWert = { bookingAccountId: string | null; costCentreId: string | null };
export type KontierungStelle = "SUPPLIER_CONTRACT" | "INCOMING_LINE" | "PRODUCT" | "ORDER_ITEM" | "INVOICE_ITEM";

const EXPENSE_POINTS: readonly KontierungStelle[] = ["SUPPLIER_CONTRACT", "INCOMING_LINE"];

export type ListClient = Pick<ApiClient, "getBookingAccounts" | "getCostCentres">;

/**
 * Die Auswahllisten der Kontierungsfelder EINMAL je Ansicht (Positionseditor, Vertragspanel, Erfassungsmaske, Reiter):
 * Jedes Feldpaar fragt selbst — gleiche Abfragen (Schlüssel = Abfrage) beantwortet dasselbe Versprechen; ein Fehlschlag
 * fällt aus dem Speicher und wird beim nächsten Mal neu gefragt. Die eine Stelle für C, D und E (Aufgabe 22).
 */
export function sharedAssignmentLists(client: ListClient): ListClient {
  const cache = new Map<string, Promise<unknown>>();
  const once = <T,>(key: string, load: () => Promise<T>): Promise<T> => {
    const known = cache.get(key);
    if (known !== undefined) return known as Promise<T>;
    const loading = load().catch((e: unknown) => { cache.delete(key); throw e; });
    cache.set(key, loading);
    return loading;
  };
  return {
    getBookingAccounts: (q) => once(`accounts ${JSON.stringify(q ?? {})}`, () => client.getBookingAccounts(q)),
    getCostCentres: (q) => once(`costCentres ${JSON.stringify(q ?? {})}`, () => client.getCostCentres(q)),
  };
}

/** Die Namen von Konto („6805 Telefon“) und Kostenstelle („IT Informatik“) für die Leseansicht. */
export type AssignmentNames = { accounts: ReadonlyMap<string, string>; costCentres: ReadonlyMap<string, string> };

/**
 * Die Namen der Kontierung EINMAL je aufgeklappter Zeile — Lieferverträge und Eingangsrechnungen teilen den Weg
 * (Final-Fixrunde A-K2): alle Aufwandskonten (auch deaktivierte, auch anderer Rahmen) und die Kostenstellen des
 * Unternehmens. `names` fehlt, solange geladen wird; `failed` nach einem Ladefehler — die Ansicht zeigt dann „–“ und
 * einen Hinweis statt ewig „…“.
 */
export function useAssignmentNames(lists: ListClient, companyId: string): { names: AssignmentNames | null; failed: boolean } {
  const [state, setState] = useState<{ names: AssignmentNames | null; failed: boolean }>({ names: null, failed: false });
  useEffect(() => {
    let alive = true;
    const label = <E extends { id: string }>(items: E[], text: (e: E) => string) => new Map(items.map((e) => [e.id, text(e)]));
    Promise.all([lists.getBookingAccounts({ kind: "EXPENSE", includeInactive: true }),
      lists.getCostCentres({ companyId, includeInactive: true })]).then(([a, k]) => {
      if (alive) {
        setState({ failed: false, names: { accounts: label(a.items, (x: Konto) => `${x.accountNumber} ${x.name}`),
          costCentres: label(k.items, (x: Kostenstelle) => `${x.code} ${x.name}`) } });
      }
    }, () => { if (alive) setState({ names: null, failed: true }); });
    return () => { alive = false; };
  }, [lists, companyId]);
  return state;
}

type Eintrag = { id: string; active: boolean };

/**
 * Die Auswahl (`list`) und ein nachgeholter gesetzter Eintrag, der in ihr fehlt (`extra`, nur solange er gesetzt ist).
 * `placeholder` beschriftet einen gesetzten Eintrag, der (noch) nicht in der Auswahl steht: „…“, solange die Liste lädt
 * oder er nachgeholt wird; „nicht mehr verfügbar“, wenn ihn auch das Nachholen nicht findet; „nicht geladen“, wenn das
 * Nachholen scheitert (Restkorrektur N4: nie dauerhaft „…“). `failed`: Liste oder Nachholen scheiterten — die Felder
 * sagen es mit einem Hinweis; die Auswahl ist dann leer.
 */
type Fetched<T> = { id: string; item: T | null; failed: boolean };
function useSelection<T extends Eintrag>(
  laden: () => Promise<{ items: T[] }>, nachladen: () => Promise<{ items: T[] }>, gesetzt: string | null,
  schluessel: string,
): { list: T[]; extra: T | null; placeholder: string | null; failed: boolean } {
  const [liste, setListe] = useState<{ items: T[]; failed: boolean } | null>(null);
  const [geholt, setGeholt] = useState<Fetched<T> | null>(null);
  useEffect(() => {
    let gilt = true;
    laden().then((r) => { if (gilt) setListe({ items: r.items, failed: false }); },
      () => { if (gilt) setListe({ items: [], failed: true }); });
    return () => { gilt = false; };
    // Neu laden nur, wenn sich Unternehmen oder Art ändern (`schluessel`), nicht bei jeder Wahl.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schluessel]);
  const list = liste?.items ?? [];
  const fehlt = liste !== null && gesetzt !== null && !list.some((e) => e.id === gesetzt);
  const zumWert = geholt !== null && geholt.id === gesetzt ? geholt : null;
  const holen = fehlt && zumWert === null;
  useEffect(() => {
    if (!holen || gesetzt === null) return undefined;
    let gilt = true;
    nachladen().then((r) => { if (gilt) setGeholt({ id: gesetzt, item: r.items.find((e) => e.id === gesetzt) ?? null, failed: false }); },
      () => { if (gilt) setGeholt({ id: gesetzt, item: null, failed: true }); });
    return () => { gilt = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [holen, gesetzt]);
  const placeholder = gesetzt === null || (liste !== null && !fehlt) ? null
    : liste === null || zumWert === null ? KONTIERUNG_TEXT.pending
      : zumWert.item !== null ? null : zumWert.failed ? KONTIERUNG_TEXT.notLoaded : KONTIERUNG_TEXT.unavailable;
  return { list, extra: fehlt ? zumWert?.item ?? null : null, placeholder,
    failed: liste?.failed === true || (fehlt && zumWert?.failed === true) };
}

const alle = <T extends Eintrag>(s: { list: T[]; extra: T | null }): T[] => (s.extra === null ? s.list : [...s.list, s.extra]);
const beschriftung = (e: Eintrag, text: string): string => (e.active ? text : `${text} (${KONTIERUNG_TEXT.inactive})`);

export function KontierungFelder(p: {
  point: KontierungStelle; companyId: string; wert: KontierungWert; onWert: (w: KontierungWert) => void;
  client: ListClient; gesperrt?: boolean;
  fehler?: { bookingAccountId?: string; costCentreId?: string };
  /** Der leise Hinweis unter den Feldern: ohne Angabe die Folgestufe (DATEV-Export), ein Text ersetzt ihn (Vorgabe am
   *  Vertrag bzw. Artikel, Final-Fixrunde A-K6). Wer viele Felder untereinander zeigt (der Positionseditor), setzt
   *  `false` und nennt ihn einmal selbst (`AssignmentHint`). */
  hint?: boolean | string;
}): ReactElement {
  const kind = EXPENSE_POINTS.includes(p.point) ? "EXPENSE" : "REVENUE";
  const konten = useSelection<Konto>(
    () => p.client.getBookingAccounts({ companyId: p.companyId, kind }),
    () => p.client.getBookingAccounts({ kind, includeInactive: true }),
    p.wert.bookingAccountId, `${p.companyId}|${kind}`);
  const stellen = useSelection<Kostenstelle>(
    () => p.client.getCostCentres({ companyId: p.companyId }),
    () => p.client.getCostCentres({ companyId: p.companyId, includeInactive: true }),
    p.wert.costCentreId, p.companyId);
  // Ein nachgeholtes Konto gehört zu einem anderen Rahmen, wenn es aktiv ist (sonst stünde es in der Liste des
  // Unternehmensrahmens) oder einen anderen Rahmen trägt als die Liste. Ein deaktiviertes Konto neben leerer Liste
  // bleibt „deaktiviert“ — dort ist der Unternehmensrahmen nicht abzulesen.
  const fremd = konten.extra !== null
    && (konten.extra.active || konten.list.some((k) => k.chartOfAccounts !== konten.extra?.chartOfAccounts));
  const kontoText = (k: Konto): string => (k === konten.extra && fremd
    ? `${k.accountNumber} ${k.name} (${KONTIERUNG_TEXT.otherChart})` : beschriftung(k, `${k.accountNumber} ${k.name}`));
  const gesperrt = p.gesperrt === true;
  // Sofort-Popup statt title (Hausregel) an der Hülle beider Felder: me-auswahl belegt ihren eigenen Tipp (gekürzter
  // Text) und gibt das Ereignis frei, wenn sie nichts zeigt. Ein leerer Text schaltet das Popup stumm.
  const popup = useSofortPopup<HTMLDivElement>(gesperrt ? KONTIERUNG_TEXT.locked : "");
  // Ein gesetzter Eintrag außerhalb der Auswahl steht mit `placeholder` da („…“, „nicht mehr verfügbar“, „nicht
  // geladen“) — me-auswahl kennt ihn sonst nicht (Warnung „unbekannter Wert“, Final-Fixrunde Teil C) und zeigte ihn leer.
  const feld = (f: keyof KontierungWert, text: string, eintraege: { id: string; text: string }[],
    placeholder: string | null) => {
    const gesetzt = p.wert[f];
    return (
    <Auswahl beschriftung={text} wert={gesetzt ?? ""} gesperrt={gesperrt}
      {...(p.fehler?.[f] !== undefined ? { fehler: p.fehler[f] } : {})}
      onGeaendert={(d) => p.onWert({ ...p.wert, [f]: d.wert === "" ? null : d.wert })}>
      <option value="">{KONTIERUNG_TEXT.none}</option>
      {placeholder !== null && gesetzt !== null && <option value={gesetzt}>{placeholder}</option>}
      {eintraege.map((e) => <option key={e.id} value={e.id}>{e.text}</option>)}
    </Auswahl>
    );
  };
  return (
    <div className="kontierung">
      <div className="kontierung__felder" ref={popup}>
        {feld("bookingAccountId", KONTIERUNG_TEXT.account,
          alle(konten).map((k) => ({ id: k.id, text: kontoText(k) })), konten.placeholder)}
        {feld("costCentreId", KONTIERUNG_TEXT.costCentre,
          alle(stellen).map((c) => ({ id: c.id, text: beschriftung(c, `${c.code} ${c.name}`) })), stellen.placeholder)}
      </div>
      {fremd && <Hinweis art="warnung">{KONTIERUNG_TEXT.otherChartHint}</Hinweis>}
      {(konten.failed || stellen.failed) && <Hinweis art="warnung">{KONTIERUNG_TEXT.listsFailed}</Hinweis>}
      {p.hint !== false && <AssignmentHint {...(typeof p.hint === "string" ? { text: p.hint } : {})} />}
    </div>
  );
}

/** DER LEISE HINWEIS zur Kontierung — eine Gestalt für Felder, Positionseditor, Rechnung und Eingangsrechnung (Final-
 *  Fixrunde Teil C, Review 22 K5): ohne Text die Folgestufe („wird mit dem DATEV-Export der Folgestufe übergeben“). */
export function AssignmentHint(p: { text?: string; className?: string }): ReactElement {
  return <p className={`kontierung__hinweis${p.className === undefined ? "" : ` ${p.className}`}`}>
    {p.text ?? KONTIERUNG_TEXT.deferred}</p>;
}
